import React from "react";
import styled, { DefaultTheme } from "styled-components";
import {
  color,
  background,
  BackgroundProps,
  space,
  SpaceProps,
  border,
  BorderProps,
  typography,
  TypographyProps,
  shadow,
  ShadowProps,
  flexbox,
  FlexboxProps,
  layout,
  LayoutProps,
  style,
  ResponsiveValue,
} from "styled-system";
import CSS from "csstype";

const textColor = style({
  prop: "textColor",
  cssProperty: "color",
  key: "colors",
});

interface ButtonSolidProps
  extends BackgroundProps,
    BorderProps,
    SpaceProps,
    TypographyProps,
    ShadowProps,
    FlexboxProps,
    LayoutProps {
  textColor?: ResponsiveValue<CSS.ColorProperty>;
  bg?: string;
  css?: string;
}

const ButtonSolid = styled.button<ButtonSolidProps>`
  padding: 0.6rem 1.5rem;
  border-radius: 5px;
  font-size: 18px;
  font-weight: 500;
  /* letter-spacing: -0.66px; */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform: perspective(1px) translateZ(0);
  position: relative;
  overflow: hidden;
  outline: none !important;
  white-space: nowrap;
  ${textColor};
  ${background};
  ${border};
  ${space};
  ${typography};
  ${shadow};
  ${flexbox};
  ${layout};
  ${color}

  &:before {
    content: "";
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.1);
    transform: scaleX(0);
    transform-origin: 100% 50%;
    transition-property: transform;
    transition-duration: 0.5s;
    transition-timing-function: ease-out;
  }

  &:hover:before,
  &:focus:before,
  &:active:before {
    transform: scaleX(1);
    transition-timing-function: cubic-bezier(0.52, 1.64, 0.37, 0.66);
  }
`;

interface ButtonOutlineProps {
  theme: DefaultTheme;
  color: keyof DefaultTheme["colors"];
}

const ButtonOutline = styled(ButtonSolid)`
  background: transparent;
  /* border: 1px solid ${({ theme, color }: ButtonOutlineProps) =>
    theme.colors[color]}; */
  /* color: ${({ theme, color }: ButtonOutlineProps) => theme.colors[color]}; */

  &:before {
    background: ${({ theme, color }: ButtonOutlineProps) =>
      theme.colors[color]};
  }

  &:hover,
  &:focus,
  &:active {
    color: ${({ theme, color }: ButtonOutlineProps) => theme.colors.light};
  }
  &:hover:before,
  &:focus:before,
  &:active:before {
    transform: scaleX(1);
    transition-timing-function: cubic-bezier(0.52, 1.64, 0.37, 0.66);
    background: ${({ theme, color }: ButtonOutlineProps) =>
      theme.colors[color]};
  }
`;

interface Props extends ButtonSolidProps {
  variant?: string;
  color?: keyof DefaultTheme["colors"];
  type?: "button" | "submit" | "reset" | undefined;
  children: React.ReactNode;
  onSubmit?: React.FormEventHandler<FormControlElement>;
}

const Button: React.FC<Props> = ({
  variant = "solid",
  color = "light",
  bg = "primary",
  ...rest
}) => {
  return variant === "solid" ? (
    <ButtonSolid
      color={"light"}
      border={`1px solid`}
      borderColor={bg}
      bg={bg}
      {...rest}
    />
  ) : (
    <ButtonOutline
      color={color}
      bg={bg}
      border={`1px solid`}
      borderColor={color}
      {...rest}
    />
  );
};

export default Button;
